import { Box, Button, Grid, IconButton, Paper, TextField, Tooltip, useTheme } from '@mui/material'

import React from 'react'
import Header from '../../Header';
import i18next from 'i18next';
import { Trans } from 'react-i18next';
import { useState } from 'react';
import { User } from '../../../types/User';
import { useNotify } from '../../Notify/NotifyProvider'
import request from '../../../helpers/myAxios'
import { useRef } from 'react';
import { useEffect } from 'react';
import UserEditorModal from './UserEditorModal';

import StyledReactDataGrid, { handleExportToCsv } from '../../tables/StyledReactDataGrid'
import { useCallback } from 'react';
import { TypeComputedProps, TypeOnSelectionChangeArg } from '@inovua/reactdatagrid-community/types/TypeDataGridProps';
import { MutableRefObject } from 'react';
import { SaveAlt } from '@mui/icons-material';
import { useConfirm } from '../../modals/ConfirmModal';

const maxViewWidth = 1200;
var doit: any;

interface UserRow {
    user: User,
    id?: string,
    firstName?: string,
    lastName?: string,
    email?: string,
    username?: string
}

interface ManageUserViewProps {
}

function ManageUserView(props: ManageUserViewProps) {
    const theme = useTheme();
    const notify = useNotify();
    const confirm = useConfirm();
    const parentRef = useRef<HTMLDivElement>(null);

    const initialFilter: any = { first_name: '', last_name: '', email: '', username: '' }
    const [gridRef, setGridRef] = useState<MutableRefObject<TypeComputedProps> | null>(null);
    const [filter, setFilter] = useState<any>(initialFilter);
    const [userRows, setUserRows] = useState<UserRow[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<any>(null);

    const [editUsers, setEditUser] = useState<User | null>(null);
    const [openUserEditor, setOpenUserEditor] = useState(false);

    const handleSearch = () => {

        let params = { ...filter };

        for (const key of Object.keys(params)) {
            if (!params[key])
                delete params[key];
        }

        request().get('/user', { params: params }).then((res) => {
            setUserRows(res.data);
        }).catch((err) => {
            if (err.response && err.response.status === 404)
                setUserRows([]);
            else
                notify.error(i18next.t('ObjectDialogs.msgNoObjectFound'));
        });
    }

    const forceRender = () => {
        setUserRows([...userRows]);
    }

    //Force recalculate table size on first render
    useEffect(() => {
        forceRender();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parentRef]);

    useEffect(() => {
        handleSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    //Force redraw on browser size change
    window.onresize = function () {
        clearTimeout(doit);
        doit = setTimeout(() => { forceRender(); }, 100);
    };

    const handleReset = () => {
        setFilter(initialFilter);
        setUserRows([]);
    }

    const handleChange = (event: any) => {
        if (event.target.id === 'typeId')
            setFilter({ [event.target.id]: event.target.value });
        else
            setFilter({ ...filter, [event.target.id]: event.target.value });
    }

    const handleKeyPress = (e: any) => {
        if (e.key === 'Enter')
            handleSearch();
    }

    const handleAdd = () => {
        setEditUser(null);
        setOpenUserEditor(true);
    }

    const handleDelete = () => {
        if (selectedUserId) {
            confirm({
                description: i18next.t('ManageUserView.msgConfirmDelete'),
                confirmationText: i18next.t('buttons.yes'),
                cancellationText: i18next.t('buttons.no')
            }).then(() => {
                request().delete('/user/' + selectedUserId).then((res) => {
                    handleSearch();
                    notify.success(i18next.t('ManageUserView.msgUserDeleted'));
                }).catch((err) => {
                    notify.error(i18next.t('ManageUserView.msgNoUserFound'));
                });
            })
        }
    }

    const handleEdit = () => {
        if (selectedUserId) {
            const userRowFound = userRows.find((userRow) => userRow.id === selectedUserId)

            if (userRowFound?.user) {
                setEditUser(userRowFound.user);
                setOpenUserEditor(true);
            }
        }
    }

    let parentHeight = 500;
    if (parentRef && parentRef.current && parentRef.current.offsetHeight)
        parentHeight = parentRef.current.offsetHeight;

    const columns = [
        { name: 'first_name', defaultFlex: 1, header: i18next.t('ManageUserView.firstName') },
        { name: 'last_name', defaultFlex: 1, header: i18next.t('ManageUserView.lastName') },
        { name: 'email', defaultFlex: 1, header: i18next.t('ManageUserView.email') },
        { name: 'username', defaultFlex: 1, header: i18next.t('ManageUserView.username') },
        { name: 'status', header: i18next.t('ManageUserView.status'), defaultWidth: 80 }
    ];

    const handleSelectionChange = useCallback(({ data, selected }: TypeOnSelectionChangeArg) => {
        setSelectedUserId(data);
    }, [])

    const onDoubleRowClick = useCallback((rowProps, event) => {
        if (rowProps.data) {
            setEditUser(rowProps.data);
            setOpenUserEditor(true);
        }
    }, [])

    return (
        <Box style={{ display: 'flex', height: '100%', width: '100%', flexFlow: 'column' }}>
            <Header title={i18next.t("ManageUserView.title")} >
                <Button sx={{ ml: 2, mr: 2 }} variant='contained' color='primary' onClick={handleAdd}><Trans i18nKey='buttons.add' /></Button>
            </Header>
            <Box sx={{ px: 2, pt: 2, pb: 1, width: '100%' }}>
                <Paper sx={{ p: 1, width: '100%' }} elevation={3}>
                    <Grid container spacing={1}>
                        <Grid item xs={4}>
                            <TextField id="first_name" fullWidth label={i18next.t('ManageUserView.firstName')} size='small'
                                value={filter.firstName} onChange={handleChange} variant="filled" onKeyDown={handleKeyPress} />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField id="last_name" fullWidth label={i18next.t('ManageUserView.lastName')} size='small'
                                value={filter.lastName} onChange={handleChange} variant="filled" onKeyDown={handleKeyPress} />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField id="username" fullWidth label={i18next.t('ManageUserView.username')} size='small'
                                value={filter.username} onChange={handleChange} variant="filled" onKeyDown={handleKeyPress} />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField id="email" fullWidth label={i18next.t('ManageUserView.email')} size='small'
                                value={filter.email} onChange={handleChange} variant="filled" onKeyDown={handleKeyPress} />
                        </Grid>

                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button variant='contained' onClick={handleSearch}><Trans i18nKey='buttons.search' /></Button>
                            <Button style={{ marginLeft: '8px' }} variant='outlined' onClick={handleReset}><Trans i18nKey='buttons.reset' /></Button>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>
            <Box sx={{ flexGrow: 1, width: '100%', height: '100%', px: 2 }} ref={parentRef}>
                <StyledReactDataGrid
                    handle={setGridRef}
                    idProperty="id"
                    columns={columns}
                    style={{ height: parentHeight - 5 }}
                    dataSource={userRows}
                    enableSelection
                    onSelectionChange={handleSelectionChange}
                    onDoubleRowClick={onDoubleRowClick}
                    selected={selectedUserId}
                />
            </Box>
            <Box sx={{ display: 'flex', pb: 1, justifyContent: 'flex-end' }}>
                <Box style={{ flexGrow: 1 }} />
                <Button disabled={selectedUserId ? false : true} sx={{ ml: '8px' }} variant='contained' onClick={handleDelete}><Trans i18nKey='buttons.delete' /></Button>
                <Button disabled={selectedUserId ? false : true} sx={{ ml: '8px' }} variant='contained' onClick={handleEdit}><Trans i18nKey='buttons.edit' /></Button>
            </Box>
            <UserEditorModal open={openUserEditor} editUser={editUsers} onAbort={() => { setOpenUserEditor(false); }} onSave={() => { setOpenUserEditor(false); handleSearch(); }} />
        </Box >
    )
}

export default ManageUserView

